import React, { useState } from "react";
import { toast } from "react-toastify";
import { registerUser } from "../../hooks/api";
import { ProgressBar, InputGroup, Form, Button } from "react-bootstrap";
import {
  getPasswordStrength,
  getPasswordColor,
  getPasswordRules,
  allRulesPassed,
} from "../../utils/passwordUtils";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function RegisterForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    department: "",
    date_of_birth: "",
    gender: "",
    position: "",
    category: "30", // ✅ Added category with default value
    password: "",
    confirm_password: "",
    role: "staff",
    profile_image: null,
  });

  const [preview, setPreview] = useState(null);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordRules = getPasswordRules(formData.password);
  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMatch =
    formData.password === formData.confirm_password && formData.password !== "";

  const isValid =
    formData.name &&
    formData.email &&
    formData.phone &&
    formData.department &&
    formData.date_of_birth &&
    formData.gender &&
    formData.position &&
    formData.category && // ✅ Added category validation
    allRulesPassed(formData.password) &&
    passwordsMatch;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, profile_image: file }));
      setPreview(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // ✅ rename to avoid overwriting state variable
    const data = new FormData();
    data.append("full_name", formData.name);
    data.append("email", formData.email);
    data.append("role", formData.role);
    data.append("phone", formData.phone);
    data.append("address", formData.address);
    data.append("department", formData.department);
    data.append("date_of_birth", formData.date_of_birth);
    data.append("gender", formData.gender);
    data.append("position", formData.position);
    data.append("category", formData.category); // ✅ Added category to FormData
    data.append("password", formData.password);
    data.append("confirm_password", formData.confirm_password);

    if (formData.profile_image)
      data.append("profile_image", formData.profile_image);

    // ✅ Log the data being sent for debugging
    console.log("Sending FormData:");
    for (let [key, value] of data.entries()) {
      console.log(key, value);
    }

    try {
      const res = await registerUser(data, true); // true = multipart

      if (res.success) {
        toast.success(
          res.message || "Registered successfully! Please login to continue."
        );
        if (onSuccess) onSuccess();
      } else {
        toast.error(res.error || "Registration failed.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Registration error:", err);
      
      // ✅ Better error handling to show the actual error message
      if (err.response && err.response.data && err.response.data.error) {
        toast.error(err.response.data.error);
      } else if (err.response && err.response.data && err.response.data.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Something went wrong. Try again.");
      }
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Name */}
      <div className="mb-3">
        <input
          className="form-control"
          type="text"
          name="name"
          placeholder="Full Name"
          value={formData.name}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        />
      </div>
      {/* Email */}
      <div className="mb-3">
        <input
          className="form-control"
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        />
      </div>
      {/* Phone */}
      <div className="mb-3">
        <input
          className="form-control"
          type="tel"
          name="phone"
          placeholder="Phone Number"
          value={formData.phone}
          onChange={handleChange}
          required
        />
      </div>
      {/* Address */}
      <div className="mb-3">
        <textarea
          className="form-control"
          name="address"
          placeholder="Address"
          rows="2"
          value={formData.address}
          onChange={handleChange}
          required
        />
      </div>
      {/* department*/}
      <div className="mb-3">
        <input
          className="form-control"
          name="department"
          placeholder="Department"
          value={formData.department}
          onChange={handleChange}
          required
        />
      </div>
      {/* ddate_of_birth*/}
      <div className="mb-3">
        <input
          className="form-control"
          type="date" // ✅ Changed to type="date" for better UX
          name="date_of_birth"
          placeholder="Date of birth"
          value={formData.date_of_birth}
          onChange={handleChange}
          required
        />
      </div>
      {/* gender*/}
      <div className="mb-3">
        <select // ✅ Changed to select for better UX
          className="form-select"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          required
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </div>
      {/*position*/}
      <div className="mb-3">
        <input
          className="form-control"
          name="position"
          placeholder="Position"
          value={formData.position}
          onChange={handleChange}
          required
        />
      </div>
      {/* Category */}
      <div className="mb-3">
        <select
          className="form-select"
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
        >
          <option value="30">30</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
      {/* Role */}
      <div className="mb-3">
        <select
          className="form-select"
          name="role"
          value={formData.role}
          onChange={handleChange}
        >
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {/* Profile Image */}
      <div className="mb-3 text-center">
        <label className="form-label fw-semibold d-block">Profile Image</label>
        <input
          className="form-control"
          type="file"
          name="profile_image"
          accept="image/*"
          onChange={handleChange}
        />
        {preview && (
          <div className="mt-3">
            <img
              src={preview}
              alt="Preview"
              className="rounded-circle border border-primary shadow-sm"
              width="100"
              height="100"
              style={{ objectFit: "cover" }}
            />
          </div>
        )}
      </div>
      {/* Password with visibility toggle */}
      <div className="mb-2">
        <InputGroup>
          <Form.Control
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          />
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </Button>
        </InputGroup>

        {formData.password && (
          <>
            <ProgressBar
              now={passwordStrength}
              variant={getPasswordColor(passwordStrength)}
              className="mt-1"
              label={
                passwordStrength === 100
                  ? "Strong"
                  : passwordStrength >= 70
                  ? "Medium"
                  : "Weak"
              }
            />
            <ul className="small mt-2 ps-3 text-muted">
              <li
                className={
                  passwordRules.length ? "text-success" : "text-danger"
                }
              >
                Minimum 6 characters
              </li>
              <li
                className={
                  passwordRules.uppercase ? "text-success" : "text-danger"
                }
              >
                At least one uppercase letter
              </li>
              <li
                className={
                  passwordRules.number ? "text-success" : "text-danger"
                }
              >
                At least one number
              </li>
              <li
                className={
                  passwordRules.special ? "text-success" : "text-danger"
                }
              >
                At least one special character
              </li>
            </ul>
          </>
        )}
      </div>

      {/* Confirm Password with toggle */}
      <div className="mb-3">
        <InputGroup>
          <Form.Control
            type={showConfirm ? "text" : "password"}
            name="confirm_password"
            placeholder="Confirm Password"
            value={formData.confirm_password}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            isInvalid={
              touched.confirm_password &&
              formData.confirm_password &&
              !passwordsMatch
            }
            isValid={passwordsMatch}
          />
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
          >
            {showConfirm ? <FaEyeSlash /> : <FaEye />}
          </Button>
        </InputGroup>
        {touched.confirm_password &&
          formData.confirm_password &&
          !passwordsMatch && (
            <div className="text-danger small mt-1">Passwords do not match</div>
          )}
      </div>

      <button
        type="submit"
        className="btn btn-primary w-100"
        disabled={!isValid || loading}
      >
        {loading ? "Registering..." : "Register"}
      </button>
    </form>
  );
}